import type { CSSProperties } from "react";

/** Cores padrão do escritório (portal externo usa; dashboard interno não aplica tema por instituição). */
export const PADRAO_PRIMARIA_ESCRITORIO = "#791614";
export const PADRAO_ACENTO_ESCRITORIO = "#d7b369";
export const PADRAO_TEXTO_ESCRITORIO = "#f9f6f0";

/** Converte #RGB ou #RRGGBB em rgba() para uso em variáveis CSS. */
export function hexParaRgba(hex: string, alpha: number): string {
  let h = hex.trim().replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length !== 6 || Number.isNaN(parseInt(h, 16))) {
    return hexParaRgba(PADRAO_PRIMARIA_ESCRITORIO, alpha);
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Mistura a cor com branco (ex.: preenchimento suave de ícones de pasta). */
export function misturarComBranco(hex: string, pesoCor: number): string {
  let h = hex.trim().replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length !== 6 || Number.isNaN(parseInt(h, 16))) {
    return "rgb(245, 230, 168)";
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const t = 1 - pesoCor;
  const R = Math.round(r * pesoCor + 255 * t);
  const G = Math.round(g * pesoCor + 255 * t);
  const B = Math.round(b * pesoCor + 255 * t);
  return `rgb(${R},${G},${B})`;
}

/** Fundo da área de conteúdo do portal externo: branco, lavado primário e toque da secundária. */
export function gradienteFundoAreaConteudoPortal(primary: string, accent: string): string {
  const p = (primary || PADRAO_PRIMARIA_ESCRITORIO).trim();
  const a = (accent || PADRAO_ACENTO_ESCRITORIO).trim();
  return [
    "linear-gradient(168deg,",
    "#ffffff 0%,",
    `${hexParaRgba(p, 0.07)} 30%,`,
    "#faf8f4 52%,",
    `${hexParaRgba(a, 0.14)} 78%,`,
    "#ffffff 100%)",
  ].join(" ");
}

/** Barra superior do portal externo: primária, secundária e branco. */
export function gradienteCabecalhoPortalExterno(primary: string, accent: string): string {
  const p = (primary || PADRAO_PRIMARIA_ESCRITORIO).trim();
  const a = (accent || PADRAO_ACENTO_ESCRITORIO).trim();
  return [
    "linear-gradient(110deg,",
    `${p} 0%,`,
    `${hexParaRgba(a, 0.42)} 38%,`,
    `${hexParaRgba(p, 0.88)} 72%,`,
    "rgba(255,255,255,0.28) 100%)",
  ].join(" ");
}

/**
 * Variáveis consumidas apenas pelas telas sob `/acesso-externo/portal/:id`.
 * O dashboard interno permanece com as cores fixas do escritório (`index.css`).
 */
export function estiloVariaveisTemaPortal(
  primary: string,
  accent: string,
  textColor?: string,
): CSSProperties {
  const p = (primary || PADRAO_PRIMARIA_ESCRITORIO).trim();
  const a = (accent || PADRAO_ACENTO_ESCRITORIO).trim();
  const tx = (textColor || PADRAO_TEXTO_ESCRITORIO).trim();
  return {
    "--pt-text": tx,
    "--pt-text-70": hexParaRgba(tx, 0.7),
    "--pt-text-55": hexParaRgba(tx, 0.55),
    "--pt-text-45": hexParaRgba(tx, 0.45),
    "--pt-text-30": hexParaRgba(tx, 0.3),
    "--pt-primary": p,
    "--pt-accent": a,
    "--pt-surface-gradient": gradienteFundoAreaConteudoPortal(p, a),
    "--pt-header-gradient": gradienteCabecalhoPortalExterno(p, a),
    "--pt-light": `${p}14`,
    "--pt-primary-06": hexParaRgba(p, 0.06),
    "--pt-primary-07": hexParaRgba(p, 0.07),
    "--pt-primary-08": hexParaRgba(p, 0.08),
    "--pt-primary-10": hexParaRgba(p, 0.1),
    "--pt-primary-15": hexParaRgba(p, 0.15),
    "--pt-primary-20": hexParaRgba(p, 0.2),
    "--pt-primary-35": hexParaRgba(p, 0.35),
    "--pt-primary-90": hexParaRgba(p, 0.9),
    "--pt-accent-08": hexParaRgba(a, 0.08),
    "--pt-accent-12": hexParaRgba(a, 0.12),
    "--pt-accent-14": hexParaRgba(a, 0.14),
    "--pt-accent-18": hexParaRgba(a, 0.18),
    "--pt-accent-22": hexParaRgba(a, 0.22),
    "--pt-accent-25": hexParaRgba(a, 0.25),
    "--pt-accent-30": hexParaRgba(a, 0.3),
    "--pt-accent-35": hexParaRgba(a, 0.35),
    "--pt-accent-40": hexParaRgba(a, 0.4),
    "--pt-accent-45": hexParaRgba(a, 0.45),
    "--pt-accent-50": hexParaRgba(a, 0.5),
    "--pt-accent-55": hexParaRgba(a, 0.55),
    "--pt-accent-60": hexParaRgba(a, 0.6),
    "--pt-accent-70": hexParaRgba(a, 0.7),
    "--pt-accent-90": hexParaRgba(a, 0.9),
    "--pt-accent-fill-soft": misturarComBranco(a, 0.34),
  } as CSSProperties;
}
